<template>
  <div class="system-settings">
    <el-tabs v-model="activeTab">
      <!-- 基本设置 -->
      <el-tab-pane label="基本设置" name="basic">
        <el-form :model="basicSettings" label-width="120px">
          <el-form-item label="系统名称">
            <el-input v-model="basicSettings.systemName" />
          </el-form-item>
          <el-form-item label="系统Logo">
            <el-upload
              class="logo-uploader"
              action="/api/upload"
              :show-file-list="false"
              :on-success="handleLogoSuccess"
              :before-upload="beforeLogoUpload"
            >
              <img v-if="basicSettings.logo" :src="basicSettings.logo" class="logo" />
              <el-icon v-else class="logo-uploader-icon"><Plus /></el-icon>
            </el-upload>
          </el-form-item>
          <el-form-item label="系统描述">
            <el-input
              v-model="basicSettings.description"
              type="textarea"
              rows="3"
            />
          </el-form-item>
          <el-form-item label="备案信息">
            <el-input v-model="basicSettings.icp" />
          </el-form-item>
          <el-form-item label="版权信息">
            <el-input v-model="basicSettings.copyright" />
          </el-form-item>
          <el-form-item>
            <el-button type="primary" @click="handleSaveBasic">保存</el-button>
          </el-form-item>
        </el-form>
      </el-tab-pane>

      <!-- 安全设置 -->
      <el-tab-pane label="安全设置" name="security">
        <el-form :model="securitySettings" label-width="120px">
          <el-form-item label="密码策略">
            <el-checkbox-group v-model="securitySettings.passwordPolicy">
              <el-checkbox label="requireNumber">必须包含数字</el-checkbox>
              <el-checkbox label="requireLetter">必须包含字母</el-checkbox>
              <el-checkbox label="requireSpecial">必须包含特殊字符</el-checkbox>
            </el-checkbox-group>
          </el-form-item>
          <el-form-item label="密码最小长度">
            <el-input-number v-model="securitySettings.minPasswordLength" :min="6" :max="20" />
          </el-form-item>
          <el-form-item label="登录失败次数">
            <el-input-number v-model="securitySettings.maxLoginAttempts" :min="3" :max="10" />
          </el-form-item>
          <el-form-item label="登录超时时间">
            <el-input-number v-model="securitySettings.sessionTimeout" :min="30" :max="1440" />
            <span class="unit">分钟</span>
          </el-form-item>
          <el-form-item label="IP白名单">
            <el-input
              v-model="securitySettings.ipWhitelist"
              type="textarea"
              rows="3"
              placeholder="每行一个IP地址"
            />
          </el-form-item>
          <el-form-item>
            <el-button type="primary" @click="handleSaveSecurity">保存</el-button>
          </el-form-item>
        </el-form>
      </el-tab-pane>

      <!-- 通知设置 -->
      <el-tab-pane label="通知设置" name="notification">
        <el-form :model="notificationSettings" label-width="120px">
          <el-form-item label="邮件通知">
            <el-switch v-model="notificationSettings.emailEnabled" />
          </el-form-item>
          <el-form-item label="SMTP服务器" v-if="notificationSettings.emailEnabled">
            <el-input v-model="notificationSettings.smtpServer" />
          </el-form-item>
          <el-form-item label="SMTP端口" v-if="notificationSettings.emailEnabled">
            <el-input-number v-model="notificationSettings.smtpPort" :min="1" :max="65535" />
          </el-form-item>
          <el-form-item label="发件人邮箱" v-if="notificationSettings.emailEnabled">
            <el-input v-model="notificationSettings.senderEmail" />
          </el-form-item>
          <el-form-item label="短信通知">
            <el-switch v-model="notificationSettings.smsEnabled" />
          </el-form-item>
          <el-form-item label="短信服务商" v-if="notificationSettings.smsEnabled">
            <el-select v-model="notificationSettings.smsProvider">
              <el-option label="阿里云" value="aliyun" />
              <el-option label="腾讯云" value="tencent" />
            </el-select>
          </el-form-item>
          <el-form-item label="通知模板">
            <el-input
              v-model="notificationSettings.template"
              type="textarea"
              rows="4"
              placeholder="请输入通知模板内容"
            />
          </el-form-item>
          <el-form-item>
            <el-button type="primary" @click="handleSaveNotification">保存</el-button>
          </el-form-item>
        </el-form>
      </el-tab-pane>

      <!-- 存储设置 -->
      <el-tab-pane label="存储设置" name="storage">
        <el-form :model="storageSettings" label-width="120px">
          <el-form-item label="存储方式">
            <el-radio-group v-model="storageSettings.type">
              <el-radio label="local">本地存储</el-radio>
              <el-radio label="oss">阿里云OSS</el-radio>
              <el-radio label="cos">腾讯云COS</el-radio>
            </el-radio-group>
          </el-form-item>
          <template v-if="storageSettings.type !== 'local'">
            <el-form-item label="Access Key">
              <el-input v-model="storageSettings.accessKey" />
            </el-form-item>
            <el-form-item label="Secret Key">
              <el-input v-model="storageSettings.secretKey" type="password" show-password />
            </el-form-item>
            <el-form-item label="Bucket">
              <el-input v-model="storageSettings.bucket" />
            </el-form-item>
            <el-form-item label="域名">
              <el-input v-model="storageSettings.domain" />
            </el-form-item>
          </template>
          <el-form-item label="文件大小限制">
            <el-input-number v-model="storageSettings.maxFileSize" :min="1" :max="100" />
            <span class="unit">MB</span>
          </el-form-item>
          <el-form-item label="允许的文件类型">
            <el-select
              v-model="storageSettings.allowedTypes"
              multiple
              placeholder="请选择"
            >
              <el-option label="图片" value="image" />
              <el-option label="文档" value="document" />
              <el-option label="视频" value="video" />
              <el-option label="音频" value="audio" />
            </el-select>
          </el-form-item>
          <el-form-item>
            <el-button type="primary" @click="handleSaveStorage">保存</el-button>
          </el-form-item>
        </el-form>
      </el-tab-pane>
    </el-tabs>
  </div>
</template>

<script setup>
import { ref, reactive } from 'vue'
import { ElMessage } from 'element-plus'
import { Plus } from '@element-plus/icons-vue'

// 当前激活的标签页
const activeTab = ref('basic')

// 基本设置
const basicSettings = reactive({
  systemName: '',
  logo: '',
  description: '',
  icp: '',
  copyright: ''
})

// 安全设置
const securitySettings = reactive({
  passwordPolicy: [],
  minPasswordLength: 8,
  maxLoginAttempts: 5,
  sessionTimeout: 60,
  ipWhitelist: ''
})

// 通知设置
const notificationSettings = reactive({
  emailEnabled: false,
  smtpServer: '',
  smtpPort: 465,
  senderEmail: '',
  smsEnabled: false,
  smsProvider: '',
  template: ''
})

// 存储设置
const storageSettings = reactive({
  type: 'local',
  accessKey: '',
  secretKey: '',
  bucket: '',
  domain: '',
  maxFileSize: 10,
  allowedTypes: []
})

// Logo上传前验证
const beforeLogoUpload = (file) => {
  const isImage = file.type.startsWith('image/')
  const isLt2M = file.size / 1024 / 1024 < 2

  if (!isImage) {
    ElMessage.error('只能上传图片文件！')
    return false
  }
  if (!isLt2M) {
    ElMessage.error('图片大小不能超过 2MB！')
    return false
  }
  return true
}

// Logo上传成功
const handleLogoSuccess = (response) => {
  basicSettings.logo = response.url
}

// 保存基本设置
const handleSaveBasic = async () => {
  try {
    // TODO: 调用保存接口
    ElMessage.success('保存成功')
  } catch (error) {
    ElMessage.error('保存失败')
  }
}

// 保存安全设置
const handleSaveSecurity = async () => {
  try {
    // TODO: 调用保存接口
    ElMessage.success('保存成功')
  } catch (error) {
    ElMessage.error('保存失败')
  }
}

// 保存通知设置
const handleSaveNotification = async () => {
  try {
    // TODO: 调用保存接口
    ElMessage.success('保存成功')
  } catch (error) {
    ElMessage.error('保存失败')
  }
}

// 保存存储设置
const handleSaveStorage = async () => {
  try {
    // TODO: 调用保存接口
    ElMessage.success('保存成功')
  } catch (error) {
    ElMessage.error('保存失败')
  }
}
</script>

<style scoped>
.system-settings {
  padding: 20px;
}

.logo-uploader {
  border: 1px dashed #d9d9d9;
  border-radius: 6px;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  width: 178px;
  height: 178px;
}

.logo-uploader:hover {
  border-color: #409EFF;
}

.logo {
  width: 178px;
  height: 178px;
  display: block;
}

.logo-uploader-icon {
  font-size: 28px;
  color: #8c939d;
  width: 178px;
  height: 178px;
  text-align: center;
  line-height: 178px;
}

.unit {
  margin-left: 10px;
  color: #909399;
}
</style> 